import React from 'react';
import { Spinner } from 'react-bootstrap';
import {
  PLAN_LIBRARY_SORTS,
  buildLibraryStats,
  buildPlanCardModel,
  filterAndSortPlans,
} from './plantingPlansLibraryUtils';
import PlantingPlantWeekOutlook from './PlantingPlantWeekOutlook';

function PlanCard({
  card,
  exporting = false,
  onToggleSelect,
  onView,
  onEdit,
  onExport,
  onShare,
  onMore,
  onMarkPlanted,
}) {
  return (
    <article
      className={`plant-plans-card${card.isSelected ? ' is-selected' : ''}`}
      data-testid={`plant-plans-card-${card.id}`}
    >
      <div
        className="plant-plans-card__head"
        style={{ background: card.headBg, color: card.headFg }}
      >
        <button
          type="button"
          className={`plant-plans-card__check${card.isSelected ? ' is-checked' : ''}`}
          aria-label={`Select ${card.name}`}
          aria-pressed={card.isSelected}
          onClick={() => onToggleSelect(card.id)}
        >
          {card.isSelected ? '✓' : ''}
        </button>
        <span className="plant-plans-card__status">{card.statusLabel}</span>
        <span className="plant-plans-card__zone">{card.zone}</span>
      </div>
      <div className="plant-plans-card__body">
        <div>
          <h3 className="plant-plans-card__name">{card.name}</h3>
          <p className="plant-plans-card__meta">{card.meta}</p>
        </div>
        <div className="plant-plans-card__chips">
          {card.chips.map((chip) => (
            <span
              key={chip.label}
              className={`plant-plans-card__chip plant-plans-card__chip--${chip.tone}`}
            >
              {chip.label}
            </span>
          ))}
        </div>
        <div className="plant-plans-card__progress">
          <div className="plant-plans-card__progress-labels">
            <span>{card.progLabel}</span>
            <span className="plant-plans-card__progress-value">{card.progValue}</span>
          </div>
          <div className="plant-plans-card__progress-track">
            <div
              className="plant-plans-card__progress-fill"
              style={{ width: `${card.progPct}%`, background: card.progFill }}
            />
          </div>
        </div>
        <p className="plant-plans-card__share">{card.shareLine}</p>
        <div className="plant-plans-card__actions">
          <button
            type="button"
            className="plant-plans-card__btn plant-plans-card__btn--plant"
            data-testid={`plant-plans-mark-planted-${card.id}`}
            onClick={() => onMarkPlanted?.(card.group)}
          >
            MARK AS PLANTED
          </button>
          <button type="button" className="plant-plans-card__btn" onClick={() => onView(card.group)}>
            VIEW
          </button>
          <button type="button" className="plant-plans-card__btn" onClick={() => onEdit(card.group)}>
            EDIT
          </button>
          <button
            type="button"
            className="plant-plans-card__btn"
            data-testid={`plant-plans-export-${card.id}`}
            disabled={exporting}
            onClick={() => onExport?.(card.group)}
          >
            {exporting ? 'EXPORTING…' : 'EXPORT'}
          </button>
          <button
            type="button"
            className="plant-plans-card__btn plant-plans-card__btn--share"
            data-testid={`plant-plans-share-${card.id}`}
            onClick={() => onShare(card.group)}
          >
            SHARE
          </button>
          <button
            type="button"
            className="plant-plans-card__btn plant-plans-card__btn--more"
            aria-label={`More actions for ${card.name}`}
            onClick={() => onMore(card.group)}
          >
            ⋯
          </button>
        </div>
      </div>
    </article>
  );
}

/**
 * My Plans library (handoff §3.2). Default planting landing.
 */
export function PlantingPlansLibrary({
  savedGroups = [],
  groupsLoading = false,
  groupError = null,
  selectedPlanIds,
  onTogglePlanSelect,
  onStartNewPlan,
  onViewGroup,
  onEditGroup,
  onExportGroup,
  exportingPlanId = null,
  onShareGroup,
  onMoreGroup,
  onMarkPlanted,
  onExportSelected,
  clubNames = {},
}) {
  const [query, setQuery] = React.useState('');
  const [sort, setSort] = React.useState('recent');
  const selectedIds = selectedPlanIds instanceof Set ? selectedPlanIds : null;

  const visiblePlans = React.useMemo(
    () => filterAndSortPlans(savedGroups, { query, sort }),
    [savedGroups, query, sort]
  );

  const stats = React.useMemo(
    () => buildLibraryStats(savedGroups, visiblePlans.length),
    [savedGroups, visiblePlans.length]
  );

  const cards = React.useMemo(
    () => visiblePlans.map((group) => buildPlanCardModel(group, {
      isSelected: Boolean(selectedIds && selectedIds.has(group.id)),
      clubNames,
    })),
    [visiblePlans, selectedIds, clubNames]
  );

  const searchActive = Boolean(query.trim());
  const isEmpty = !groupsLoading && !groupError && visiblePlans.length === 0;
  const emptyBecauseSearch = isEmpty && savedGroups.length > 0 && searchActive;
  const selectedCount = selectedIds ? selectedIds.size : 0;

  return (
    <section className="plant-plans-library" data-testid="planting-plans-library">
      <button
        type="button"
        className="plant-plans-new"
        data-testid="plant-plans-start-new"
        onClick={onStartNewPlan}
      >
        <span className="plant-plans-new__plus" aria-hidden="true">+</span>
        <span className="plant-plans-new__copy">
          <span className="plant-plans-new__title">Start a new plan</span>
          <span className="plant-plans-new__kicker">FIVE STEPS · GROUND, BLEND, TIMING</span>
        </span>
        <span className="plant-plans-new__chevron" aria-hidden="true">›</span>
      </button>

      <div className="plant-plans-stats" data-testid="plant-plans-stats">
        {stats.map((tile) => (
          <div
            key={tile.id}
            className={`plant-plans-stat plant-plans-stat--${tile.tone}`}
          >
            <div className="plant-plans-stat__label">{tile.label}</div>
            <div className="plant-plans-stat__value">{tile.value}</div>
            <div className="plant-plans-stat__note">{tile.note}</div>
          </div>
        ))}
      </div>

      {!groupsLoading && !groupError && savedGroups.length > 0 ? (
        <PlantingPlantWeekOutlook plans={savedGroups} />
      ) : null}

      <div className="plant-plans-controls" data-testid="plant-plans-controls">
        <div className="plant-plans-controls__row">
          <input
            type="search"
            className="plant-plans-search"
            placeholder="Search plans, plots or blends"
            aria-label="Search plans"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="plant-plans-sort" role="group" aria-label="Sort plans">
            {PLAN_LIBRARY_SORTS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`plant-plans-sort__btn${sort === option.id ? ' is-active' : ''}`}
                aria-pressed={sort === option.id}
                onClick={() => setSort(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groupsLoading ? (
        <div className="plant-plans-empty plant-plans-empty--inline">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading plans…
        </div>
      ) : null}

      {groupError ? (
        <p className="plant-plans-empty plant-plans-empty--error" role="alert">
          {groupError}
        </p>
      ) : null}

      {isEmpty ? (
        <div className="plant-plans-empty" data-testid="plant-plans-empty">
          <div className="plant-plans-empty__title">
            {emptyBecauseSearch ? 'No plans match' : 'No plans yet'}
          </div>
          <p className="plant-plans-empty__text">
            {emptyBecauseSearch
              ? 'Try clearing search to see your saved plans again.'
              : 'Start a new plan to pick ground, build a blend, and save it here.'}
          </p>
          <button
            type="button"
            className="plant-plans-empty__cta"
            onClick={emptyBecauseSearch
              ? () => setQuery('')
              : onStartNewPlan}
          >
            {emptyBecauseSearch ? 'CLEAR SEARCH' : 'START A NEW PLAN'}
          </button>
        </div>
      ) : (
        <div className="plant-plans-grid" data-testid="plant-plans-grid">
          {cards.map((card) => (
            <PlanCard
              key={card.id}
              card={card}
              onToggleSelect={onTogglePlanSelect}
              onView={onViewGroup}
              onEdit={onEditGroup}
              onExport={onExportGroup}
              exporting={String(exportingPlanId) === String(card.id)}
              onShare={onShareGroup}
              onMore={onMoreGroup}
              onMarkPlanted={onMarkPlanted}
            />
          ))}
        </div>
      )}

      {selectedCount > 0 ? (
        <div className="plant-plans-export-bar" data-testid="plant-plans-export-bar">
          <button
            type="button"
            className="plant-plans-export-bar__btn"
            onClick={onExportSelected}
          >
            {`EXPORT LIST FOR ${selectedCount} PLAN${selectedCount === 1 ? '' : 'S'}`}
          </button>
        </div>
      ) : null}
    </section>
  );
}
